import LocalizedTextField from '@components/Customization/LocalizedTextField';
import RuleEditor, { type RuleCandidate } from '@components/Customization/PoiTypes/RuleEditor';
import { readVisibility, writeGroupVisibility } from '@lib/customization/poiDesign';
import { useLocalization } from '@lib/localization';
import { Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { hasText, type PoiFieldGroup } from '@store/api/poiTypeEndpoints';
import { LayerGroupIcon, PlusIcon, TrashIcon } from 'react-line-awesome';
import ConditionBuilder from './ConditionBuilder';
import { addButtonStyles, sectionTitleStyles } from './designer.styles';
import { type Languages } from './FieldHeading';

interface Props {
  group: PoiFieldGroup;
  candidates: ReadonlyMap<string, RuleCandidate>;
  languages: Languages;
  /** Merges into the group as the draft holds it now, so two edits in one event both land. */
  onPatch: (changes: Partial<PoiFieldGroup>) => void;
  onAddField: () => void;
  onDelete: () => void;
  disabled: boolean;
}

/**
 * A group's name and when it is shown. A group's rules switch `visible` and nothing else: the
 * other flags would cascade onto its fields, each in its own way.
 */
const GroupInspector: React.FC<Props> = ({ group, candidates, languages, onPatch, onAddField, onDelete, disabled }) => {
  const { t } = useLocalization();

  const visibility = readVisibility(group);
  // The group's own fields cannot decide whether it is shown: hiding the group hides them too.
  const ownIds = new Set(group.fields.map((field) => field.id));
  const others = [...candidates.values()].filter((candidate) => !ownIds.has(candidate.id));

  return (
    <Box sx={{ pb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 2, pt: 1.75 }}>
        <Box sx={{ display: 'inline-flex', fontSize: 20, color: 'text.secondary' }}><LayerGroupIcon /></Box>
        <Typography sx={{ fontWeight: 600, flex: 1 }}>{t('Customization_Group')}</Typography>
        <Tooltip title={t('Common_Delete')}><span><IconButton size="small" disabled={disabled} onClick={onDelete}><TrashIcon /></IconButton></span></Tooltip>
      </Stack>

      <Stack spacing={1.5} sx={{ px: 2, pt: 2 }}>
        <LocalizedTextField
          label={t('Customization_GroupName')}
          value={group.name}
          disabled={disabled}
          focusLocale={languages.locale === languages.primary ? undefined : languages.locale}
          autoFocus={!hasText(group.name)}
          onChange={(name) => onPatch({ name })}
        />
        <TextField
          size="small" label={t('Common_Description')} disabled={disabled} value={group.description ?? ''}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(event) => onPatch({ description: event.target.value || undefined })}
        />
        <Box>
          <Button size="small" startIcon={<PlusIcon />} disabled={disabled} sx={addButtonStyles} onClick={onAddField}>{t('Customization_AddField')}</Button>
        </Box>
      </Stack>

      <Box sx={{ px: 2, pt: 2 }}>
        <Typography sx={{ ...sectionTitleStyles, mb: 1.25 }}>{t('Customization_Designer_WhenShownGroup')}</Typography>
        {visibility.mode === 'advanced'
          ? (
            <RuleEditor
              rules={group.rules ?? []}
              onChange={(rules) => onPatch({ rules: rules.length ? rules as PoiFieldGroup['rules'] : undefined })}
              candidates={others}
              targets={['visible']}
              baseVisible={group.visible !== false}
              onBaseVisibleChange={(visible) => onPatch({ visible })}
              disabled={disabled}
            />
          )
          : (
            <ConditionBuilder
              visibility={visibility}
              candidates={others}
              resolve={languages.resolve}
              disabled={disabled}
              onChange={(next) => onPatch(writeGroupVisibility(group, next))}
            />
          )}
      </Box>
    </Box>
  );
};

export default GroupInspector;
